/**
 * Invoices > Dunning (tracker item 36).
 *
 * The customers a run would remind now (one email each, at the highest step
 * any of their invoices reached, listing every overdue invoice), the other
 * customers with overdue invoices and why they are not reminded, and the run
 * history. The office selects customers (all by default) and sends: each is
 * queued through the notification outbox. Shown in every mode: in OFF mode it
 * is the manual way to send reminders. Reminders only: no late fees.
 *
 * Route-gated to ADMIN / OFFICE_MANAGER, like the backend
 * requireRole('ADMIN', 'OFFICE_MANAGER') on /api/dunning.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, IDunningApi } from '../api';
import * as types from '../api/types';
import { primaryButtonClass, secondaryButtonClass } from '../components/formStyles';
import { invoiceDate, invoiceMoney } from './invoiceUi';
import { toggleSelection } from './financeChargeUi';
import {
  DUNNING_SETTINGS_PATH,
  MODE_LABELS,
  TRIGGER_LABELS,
  defaultCustomerSelection,
  dunningError,
  dunningModeDescription,
  dunningReasonLabel,
  rowNote,
  runConfirmMessage,
  selectedOverdueTotal,
  stepLabel,
  toggleAllCustomers,
} from './dunningUi';
import { Toast } from '../components/Toast';

const cell = (numeric = false) => `px-4 py-3 align-top ${numeric ? 'text-right tabular-nums whitespace-nowrap' : 'text-left'}`;
const RUN_HISTORY_LIMIT = 10;

function InvoiceList({ invoices }: { invoices: types.DunningInvoiceRef[] }) {
  return (
    <ul className="space-y-0.5">
      {invoices.map((invoice) => (
        <li key={invoice.invoiceId} className={invoice.listed ? '' : 'text-gray-400'}>
          <Link to={`/invoices/${invoice.invoiceId}`} className="text-blue-700 hover:underline">#{invoice.invoiceNumber}</Link>
          {invoice.invoiceType === 'FINANCE_CHARGE' && <span className="text-gray-500"> (late fee)</span>}
          <span className="text-gray-600"> · {invoice.daysOverdue} days · {invoiceMoney(invoice.balance)}</span>
          {invoice.excludedReason && <span className="text-gray-500"> · {dunningReasonLabel(invoice.excludedReason)}</span>}
        </li>
      ))}
    </ul>
  );
}

function RunResult({ result }: { result: types.DunningRunResult }) {
  const queued = result.results.filter((r) => r.status === 'QUEUED');
  const other = result.results.filter((r) => r.status !== 'QUEUED');
  return (
    <section aria-label="Run result" className="mb-6 rounded-lg bg-white p-4 sm:p-5 shadow break-words">
      <h2 className="text-lg font-semibold text-gray-900">Run result</h2>
      <Toast kind={queued.length ? 'success' : 'info'}>
        {queued.length === 0 ? 'No reminders were queued.' : `Queued ${queued.length} ${queued.length === 1 ? 'reminder' : 'reminders'}.`}
      </Toast>
      <p className={`mt-2 ${queued.length ? 'text-green-800' : 'text-gray-700'}`}>
        {queued.length === 0 ? 'No reminders were queued.' : `Queued ${queued.length} ${queued.length === 1 ? 'reminder' : 'reminders'}.`}
      </p>
      {queued.length > 0 && (
        <ul className="mt-3 space-y-1 text-sm">
          {queued.map((entry) => (
            <li key={entry.customerId}>
              <Link to={`/customers/${entry.customerId}`} className="font-semibold text-blue-700 hover:underline">{entry.customerName}</Link>
              {' '}step {entry.stepPosition}: {entry.invoiceNumbers.map((n) => `#${n}`).join(', ')}
              {entry.totalOverdue && <span className="text-gray-600"> ({invoiceMoney(entry.totalOverdue)})</span>}
            </li>
          ))}
        </ul>
      )}
      {other.length > 0 && (
        <div className="mt-4">
          <h3 className="text-sm font-semibold text-gray-900">Not sent ({other.length})</h3>
          <ul className="mt-1 space-y-1 text-sm">
            {other.map((entry) => (
              <li key={entry.customerId} className={entry.status === 'FAILED' ? 'text-red-700' : 'text-gray-700'}>
                {entry.customerName || entry.customerId}: {entry.status === 'FAILED' ? 'Failed (retried next run)' : dunningReasonLabel(entry.reason)}
                {entry.message && <span className="text-gray-500"> ({entry.message})</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

export function DunningPage() {
  const api = useMemo<IDunningApi>(() => createAPIClient(), []);
  const [preview, setPreview] = useState<types.DunningPreview | null>(null);
  const [runs, setRuns] = useState<types.DunningRunSummary[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<types.DunningRunResult | null>(null);
  const requestSeq = useRef(0);

  const load = useCallback(async (options: { keepResult?: boolean } = {}) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    if (!options.keepResult) setResult(null);
    try {
      const [next, history] = await Promise.all([api.previewDunning(), api.listDunningRuns(RUN_HISTORY_LIMIT)]);
      if (seq !== requestSeq.current) return;
      setPreview(next);
      setRuns(history);
      setSelected(defaultCustomerSelection(next.customers));
    } catch (cause) {
      if (seq !== requestSeq.current) return;
      setPreview(null);
      setSelected([]);
      setError(dunningError(cause, 'Could not load the dunning preview'));
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = preview?.customers ?? [];
  const ids = rows.map((row) => row.customerId);
  const selectedIds = selected.filter((id) => ids.includes(id));
  const total = selectedOverdueTotal(rows, selectedIds);
  const allSelected = ids.length > 0 && selectedIds.length === ids.length;
  const busy = loading || running;

  async function handleRun() {
    if (!preview || selectedIds.length === 0 || running) return;
    if (!window.confirm(runConfirmMessage(selectedIds.length, total))) return;
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      // Only the customers the user reviewed
      const outcome = await api.runDunning({ customerIds: selectedIds });
      setResult(outcome);
      await load({ keepResult: true });
    } catch (cause) {
      setError(dunningError(cause, 'Could not send the reminders'));
    } finally {
      setRunning(false);
    }
  }

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <Link to="/invoices" className="text-sm text-blue-700 hover:underline">← Invoices</Link>
      <h1 className="text-2xl font-bold text-gray-900 mt-2">Dunning (overdue reminders)</h1>
      <p className="mt-1 mb-4 max-w-3xl text-sm text-gray-600">
        Customers with overdue invoices get one reminder email per step of the schedule, listing every overdue invoice with a link to
        view it. Each invoice is reminded once per step; a customer is never sent two reminders within the minimum days between
        reminders. Reminders never add late fees (use Finance charges for that).
      </p>

      {preview && (
        <div role="note" className="mb-6 max-w-3xl rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
          <p>
            <strong>Mode: {MODE_LABELS[preview.mode] ?? preview.mode}.</strong> {dunningModeDescription(preview.mode, preview.hour)}
          </p>
          <p className="mt-1">
            {preview.startDate ? <>Only invoices due on or after {invoiceDate(preview.startDate)} are included. </> : null}
            At most one reminder every {preview.minDaysBetween} days per customer.
          </p>
          <Link to={DUNNING_SETTINGS_PATH} className="mt-1 inline-block font-semibold underline">Dunning settings</Link>
        </div>
      )}

      {error && <Toast kind="error">{error}</Toast>}
      {result && <RunResult result={result} />}

      {loading && !preview && <p className="text-gray-600">Loading preview…</p>}

      {preview && (
        <>
          <section aria-label="Reminders to send" className="rounded-lg bg-white p-4 sm:p-5 shadow">
            <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-lg font-semibold text-gray-900">Due a reminder as of {invoiceDate(preview.asOfDate)}</h2>
              <button type="button" className={`${secondaryButtonClass} min-h-[44px] sm:min-h-0`} disabled={busy} onClick={() => void load()}>
                {loading ? 'Loading…' : 'Refresh'}
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="responsive-table min-w-full divide-y divide-gray-200 text-sm">
                <caption className="sr-only">Customers dunning would remind now</caption>
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        disabled={busy || ids.length === 0}
                        onChange={() => setSelected(toggleAllCustomers(rows, selectedIds))}
                        aria-label="Select all customers"
                        className="h-4 w-4 rounded border-gray-300"
                      />
                    </th>
                    {[
                      { label: 'Customer' },
                      { label: 'Reminder' },
                      { label: 'Overdue invoices' },
                      { label: 'Total overdue', numeric: true },
                      { label: 'Email' },
                    ].map((column) => (
                      <th key={column.label} scope="col" className={`px-4 py-3 font-medium text-gray-600 ${column.numeric ? 'text-right' : 'text-left'}`}>
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-10 text-center text-gray-600">No customer is due a reminder.</td>
                    </tr>
                  ) : rows.map((row) => (
                    <tr key={row.customerId}>
                      <td className="px-4 py-3 w-10 align-top">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(row.customerId)}
                          disabled={busy}
                          onChange={() => setSelected((current) => toggleSelection(current, row.customerId))}
                          aria-label={`Remind ${row.customerName}`}
                          className="h-5 w-5 sm:h-4 sm:w-4 rounded border-gray-300"
                        />
                      </td>
                      <td data-label="Customer" data-primary className={cell()}>
                        <div className="min-w-0 break-words">
                          <Link to={`/customers/${row.customerId}`} className="font-semibold text-blue-700 hover:underline">{row.customerName}</Link>
                          {row.lastRemindedDate && <span className="block text-xs font-normal text-gray-500">Last reminded {invoiceDate(row.lastRemindedDate)}</span>}
                        </div>
                      </td>
                      <td data-label="Reminder" className={`${cell()} whitespace-nowrap`}>{stepLabel(row.step)}</td>
                      <td data-label="Overdue invoices" className={`${cell()} break-words`}><InvoiceList invoices={row.invoices} /></td>
                      <td data-label="Total overdue" className={`${cell(true)} font-semibold`}>{invoiceMoney(row.totalOverdue)}</td>
                      <td data-label="Email" className={cell()}>
                        {row.willSend
                          ? <span className="text-green-800">Will be sent</span>
                          : <span className="text-amber-800" title={row.message ?? undefined}>Not sent: {dunningReasonLabel(row.reason)}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-5 flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-gray-700" aria-live="polite">
                {ids.length === 0
                  ? 'Nobody to remind.'
                  : <>Selected <strong>{selectedIds.length}</strong> of {ids.length} · Overdue <strong>{invoiceMoney(total)}</strong></>}
              </p>
              <button type="button" disabled={busy || selectedIds.length === 0} onClick={() => void handleRun()} className={`${primaryButtonClass} w-full min-h-[44px] sm:w-auto sm:min-h-0`}>
                {running ? 'Sending…' : 'Send reminders'}
              </button>
            </div>
          </section>

          <section aria-label="Not reminded" className="mt-6 rounded-lg bg-white p-4 sm:p-5 shadow">
            <h2 className="text-lg font-semibold text-gray-900">Not reminded now ({preview.skipped.length})</h2>
            <p className="mt-1 text-sm text-gray-600">Other customers with overdue invoices, and why no reminder goes out now.</p>
            {preview.skipped.length === 0 ? (
              <p className="mt-3 text-sm text-gray-600">None.</p>
            ) : (
              <ul className="mt-3 divide-y divide-gray-100 text-sm">
                {preview.skipped.map((row) => (
                  <li key={row.customerId} className="py-2 flex flex-wrap justify-between gap-2">
                    <span className="min-w-0 break-words">
                      <Link to={`/customers/${row.customerId}`} className="text-blue-700 hover:underline">{row.customerName}</Link>
                      <span className="text-gray-500"> · {invoiceMoney(row.totalOverdue)} · oldest {row.oldestDaysOverdue} days</span>
                    </span>
                    <span className="font-medium text-gray-700" title={row.message ?? undefined}>
                      {dunningReasonLabel(row.reason)}
                      {rowNote(row) && row.reason === 'RECENTLY_REMINDED' && <span className="font-normal text-gray-500"> ({rowNote(row)})</span>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section aria-label="Run history" className="mt-6 rounded-lg bg-white p-4 sm:p-5 shadow">
            <h2 className="text-lg font-semibold text-gray-900">Recent runs</h2>
            {runs.length === 0 ? (
              <p className="mt-3 text-sm text-gray-600">No runs yet.</p>
            ) : (
              <table className="responsive-table mt-3 min-w-full divide-y divide-gray-200 text-sm">
                <caption className="sr-only">Recent dunning runs</caption>
                <thead className="bg-gray-50">
                  <tr>
                    {['When', 'Trigger', 'Queued', 'Not sent', 'Failed', 'Notes'].map((label) => (
                      <th key={label} scope="col" className="px-4 py-2 text-left font-medium text-gray-600">{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {runs.map((run) => (
                    <tr key={run.id}>
                      <td data-label="When" data-primary className="px-4 py-2 whitespace-nowrap">{new Date(run.startedAt).toLocaleString()}</td>
                      <td data-label="Trigger" className="px-4 py-2">{TRIGGER_LABELS[run.trigger] ?? run.trigger}</td>
                      <td data-label="Queued" className="px-4 py-2 tabular-nums">{run.queuedCount}</td>
                      <td data-label="Not sent" className="px-4 py-2 tabular-nums">{run.skippedCount}</td>
                      <td data-label="Failed" className={`px-4 py-2 tabular-nums ${run.failedCount > 0 ? 'text-red-700 font-semibold' : ''}`}>{run.failedCount}</td>
                      <td data-label="Notes" className="px-4 py-2 text-gray-600 break-words">
                        {run.error ?? run.results
                          .filter((r) => r.status === 'FAILED')
                          .map((r) => `${r.customerName}: ${r.message ?? 'failed'}`)
                          .join('; ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </main>
  );
}
